package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.rememberSession
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LSpinner
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.labelInSentence
import dev.lingvo.site.pages.auth.InvitationHandoff
import dev.lingvo.site.pages.auth.PENDING_INVITATION_KEY
import dev.lingvo.site.pages.auth.invitationAuthFormPath
import kotlinx.browser.document
import kotlinx.browser.localStorage

/**
 * Where an invitation link lands.
 *
 * Accepting needs an account, and the person clicking may not have one yet. Rather than losing the
 * token to a sign-up redirect, it is parked in local storage and picked up again afterwards — the
 * invitation is the reason they are here, and asking them to find the email a second time is the
 * fastest way to lose them.
 *
 * Authentication returns through the exact `/invite` continuation, without forwarding the token.
 * Joining remains explicit. Its committed receipt survives a failed membership read, while the
 * workspace is selectable only after current access has been verified for the same account.
 */
@Page("/invite")
@Composable
fun InvitePage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val session = rememberSession()
    val queryToken = ctx.route.queryParams["token"].orEmpty()
    val token = remember(ctx.route.path, queryToken) {
        queryToken.ifBlank { localStorage.getItem(PENDING_INVITATION_KEY).orEmpty() }
    }
    val handoff = remember(token, Session.token) { InvitationHandoff(token, scope) }
    val accepted = handoff.accepted
    val apiError = handoff.apiError

    LaunchedEffect(Unit) { document.title = tr("invite.join_a_workspace_lingvo", "Join a workspace — Lingvo") }
    LaunchedEffect(token) {
        if (token.isNotBlank()) localStorage.setItem(PENDING_INVITATION_KEY, token)
    }

    // **`Loading` is not `signed out`.** `Session.state` starts at `Loading` whenever a token is in
    // local storage, so collapsing the three arms into one boolean drew "Create an account" and "I
    // already have one" to an already-signed-in translator for the length of the `/auth/me` round
    // trip — two buttons that navigate away from the invitation they came to accept.
    val signedIn = session is SessionState.Authenticated
    val checkingSession = session is SessionState.Loading

    AuthPageScaffold(
        title = accepted?.let {
            tf("invite.you_have_joined_workspace", "You have joined {workspace}", "workspace" to it.organization.name)
        } ?: t("invite.you_have_been_invited", "You have been invited"),
        subtitle = when {
            // The role was in the response and thrown away, so "You have joined Acme" never said
            // as what — on the one screen where a person finds out what they may now do.
            accepted != null && handoff.canOpen -> tf(
                "invite.youre_in_as_a_role_the",
                "You are in as a {role}. The workspace is ready when you are.",
                "role" to (handoff.currentMembership?.role ?: accepted.role).labelInSentence(),
            )

            accepted != null && handoff.refreshing -> t(
                "invite.confirming_workspace_access",
                "Your invitation was accepted. Confirming your current workspace access.",
            )

            accepted != null -> t(
                "invite.accepted_retry_workspace_setup",
                "Your invitation was accepted. Retry the workspace setup to continue.",
            )

            token.isBlank() -> t("invite.this_link_is_missing_its_token", "This link is missing its token.")

            checkingSession -> t("invite.checking_your_session", "Checking your session.")

            signedIn -> Session.user?.name?.let { name ->
                tf("invite.accept_to_join_as_name", "Accept to join as {name}.", "name" to name)
            } ?: t("invite.accept_to_join_this_workspace", "Accept to join this workspace.")

            else -> t(
                "invite.sign_in_or_create_an_account",
                "Sign in or create an account. We will bring you back here to accept. The " +
                    "invitation stays in this browser until you accept it.",
            )
        },
        alternatePrompt = t("invite.not_expecting_this", "Not expecting this?"),
        alternateAction = t("invite.go_to_lingvo", "Go to Lingvo"),
        alternatePath = "/",
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            when {
                accepted != null -> {
                    if (handoff.refreshing) LSpinner(
                        label = t("invite.checking_workspace_access", "Checking workspace access"),
                    )
                    handoff.setupProblem?.let { problem ->
                        LAlert(
                            title = t("invite.workspace_setup_needs_another_check", "Workspace setup needs another check"),
                            description = problem,
                            variant = LAlertVariant.WARNING,
                            live = true,
                        )
                        LButton(
                            label = t("invite.retry_workspace_setup", "Retry workspace setup"),
                            onClick = handoff::retryWorkspaceSetup,
                            modifier = Modifier.fillMaxWidth(),
                            variant = LButtonVariant.SECONDARY,
                        )
                    }
                    LButton(
                        label = t("invite.open_the_workspace", "Open the workspace"),
                        onClick = { handoff.openWorkspace { ctx.router.tryRoutingTo("/app", UpdateHistoryMode.REPLACE) } },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = handoff.canOpen,
                    )
                }

                token.isBlank() -> LAlert(
                    title = t("invite.this_link_is_incomplete", "This link is incomplete"),
                    description = t(
                        "invite.open_the_invitation_link_from_your",
                        "Open the invitation link from your email again, or ask whoever invited " +
                            "you to send a new one.",
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.WARNING,
                )

                checkingSession -> LSpinner(
                    label = t("invite.checking_your_session_label", "Checking your session"),
                )

                signedIn -> {
                    if (apiError != null) {
                        LAlert(
                            title = t("invite.couldnt_accept_this_invitation", "Couldn't accept this invitation"),
                            description = apiError.orEmpty(),
                            modifier = Modifier.fillMaxWidth(),
                            variant = LAlertVariant.DANGER,
                            // It appears after paint, in answer to the button above it.
                            live = true,
                        )
                    }
                    LButton(
                        label = t("invite.accept_invitation", "Accept invitation"),
                        onClick = handoff::accept,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !handoff.accepting,
                        loading = handoff.accepting,
                    )
                }

                else -> {
                    LButton(
                        label = t("invite.create_an_account", "Create an account"),
                        onClick = { ctx.router.tryRoutingTo(invitationAuthFormPath("/signup", "/invite")) },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    LButton(
                        label = t("invite.i_already_have_one", "I already have one"),
                        onClick = { ctx.router.tryRoutingTo(invitationAuthFormPath("/login", "/invite")) },
                        modifier = Modifier.fillMaxWidth(),
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            }
        }
    }
}

